import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../axiosConfig';
import { GuideButton } from './GuideModal';
import './ConversationsSection.css';

// Icons used in this component
const Icons = {
  Message: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  ),
  ChevronLeft: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
};

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

function ConversationsSection({ conversations, onSelectConversation, selectedConversation, loading, sidebarWidth = 280 }) {
  const isMobile = useIsMobile();
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  const [pdfViewerName, setPdfViewerName] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [filterMode, setFilterMode] = useState('active');  // 'all' | 'active' | 'in_conversation' | 'awaiting_reply' | 'new'
  const [sortMode, setSortMode] = useState('recent');       // 'recent' | 'name'
  const [showFilterInfo, setShowFilterInfo] = useState(false);
  const filterInfoRef = useRef(null);
  const [showProspectPanel, setShowProspectPanel] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [memberModal, setMemberModal] = useState(null);  // { requestId, month, year }

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.interaction_request_id);
      if (!isMobile) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
    setShowProspectPanel(false);
  }, [selectedConversation, isMobile]);

  // Close filter info popover on click outside
  useEffect(() => {
    if (!showFilterInfo) return;
    const handleClickOutside = (e) => {
      if (filterInfoRef.current && !filterInfoRef.current.contains(e.target)) {
        setShowFilterInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterInfo]);

  const formatIncomeRange = (range) => {
    if (!range) return "N/A";
    if (range === "1000000+") return "$1M+";
    const [low, high] = range.split("-");
    const formattedHigh = high === "1000000" ? "$1M" : `$${(high / 1000).toFixed()}K`;
    return `$${(low / 1000).toFixed()}K - ${formattedHigh}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, '');
    const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)} - ${d.slice(6)}`;
    return phone;
  };

  // Pipeline stage config (matches ClubDashboard)
  const stageColors = {
    warm_lead: { bg: '#FAF6F1', color: '#9A7B5B', border: '#C9A87C' },
    waitlist: { bg: '#f0f9ff', color: '#0284c7', border: '#7dd3fc' },
    retired: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
    member: { bg: '#e8f4f4', color: '#346566', border: '#4a8a8b' },
  };

  const stageOptions = [
    { value: 'warm_lead', label: 'Warm Lead' },
    { value: 'waitlist', label: 'Added to Waitlist' },
    { value: 'retired', label: 'Retire Lead — Will Not Join' },
    { value: 'member', label: 'Member — Joined Club' },
  ];

  const savePipelineStage = async (requestId, stage, month, year) => {
    const payload = { stage };
    if (stage === 'member') {
      payload.member_since_month = month;
      payload.member_since_year = year;
    }

    // Optimistically update the conversation in parent state
    const prev = selectedConversation?.pipeline_stage;
    if (selectedConversation) selectedConversation.pipeline_stage = stage;

    try {
      await axiosInstance.patch(`myclub/requests/${requestId}/pipeline/`, payload);
    } catch (error) {
      // Revert on error
      if (selectedConversation) selectedConversation.pipeline_stage = prev;
      const msg = error.response?.data?.error || "Failed to update stage.";
      alert(msg);
    }
  };

  const handleStageChange = (requestId, stage) => {
    if (stage === 'member') {
      const now = new Date();
      setMemberModal({ requestId, month: now.getMonth() + 1, year: now.getFullYear() });
    } else {
      savePipelineStage(requestId, stage);
    }
  };

  const handleMemberConfirm = () => {
    if (memberModal) {
      savePipelineStage(memberModal.requestId, 'member', memberModal.month, memberModal.year);
      setMemberModal(null);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 1500);
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 1500);
    });
  };

  const loadMessages = async (requestId) => {
    setMessagesLoading(true);
    try {
      const response = await axiosInstance.get(`club-interface/conversations/${requestId}/messages/`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
    setMessagesLoading(false);
  };

  const sendMessage = async () => {
    if ((!messageInput.trim() && !attachedFile) || !selectedConversation || sending) return;

    setSending(true);
    try {
      const url = `club-interface/conversations/${selectedConversation.interaction_request_id}/send/`;

      if (attachedFile) {
        const formData = new FormData();
        formData.append('attachment', attachedFile);
        if (messageInput.trim()) {
          formData.append('content', messageInput);
        }
        await axiosInstance.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axiosInstance.post(url, { content: messageInput });
      }

      setMessageInput("");
      setAttachedFile(null);
      loadMessages(selectedConversation.interaction_request_id);
    } catch (error) {
      const errorMsg = error.response?.data?.error;
      if (errorMsg) {
        alert(errorMsg);
      }
      console.error("Failed to send message:", error);
    }
    setSending(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      alert('File size must be under 25 MB.');
      return;
    }
    setAttachedFile(file);
    e.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (diffDays === 0) return time;
    if (diffDays === 1) return `Yesterday ${time}`;
    if (diffDays < 7) return `${date.toLocaleDateString([], { weekday: 'short' })} ${time}`;
    return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  };

  const formatDateSeparator = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const formatConversationTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const shouldShowDateSeparator = (currentMsg, prevMsg) => {
    if (!prevMsg) return true;
    const currentDate = new Date(currentMsg.timestamp).toDateString();
    const prevDate = new Date(prevMsg.timestamp).toDateString();
    return currentDate !== prevDate;
  };

  const isGroupedMessage = (currentMsg, prevMsg) => {
    if (!prevMsg) return false;
    if (currentMsg.is_sent_by_club !== prevMsg.is_sent_by_club) return false;
    const timeDiff = new Date(currentMsg.timestamp) - new Date(prevMsg.timestamp);
    return timeDiff < 2 * 60 * 1000;
  };

  const renderMessages = () => {
    // Find the index of the very last club-sent message (for read receipt)
    const lastClubMsgIndex = messages.reduce((last, m, i) => m.is_sent_by_club ? i : last, -1);

    return messages.map((msg, index) => {
      const prevMsg = messages[index - 1];
      const nextMsg = messages[index + 1];
      const showDateSeparator = shouldShowDateSeparator(msg, prevMsg);
      const isGrouped = isGroupedMessage(msg, prevMsg);
      const isLastInGroup = !nextMsg || !isGroupedMessage(nextMsg, msg);

      // Show read receipt only on the very last club-sent message in the conversation
      const showReadReceipt = msg.is_sent_by_club && index === lastClubMsgIndex;

      return (
        <React.Fragment key={msg.id || index}>
          {showDateSeparator && (
            <div className="piq-msg__date-separator">
              <span>{formatDateSeparator(msg.timestamp)}</span>
            </div>
          )}
          <div className={`piq-msg__message ${msg.is_sent_by_club ? 'piq-msg__message--sent' : 'piq-msg__message--received'} ${isGrouped ? 'piq-msg__message--grouped' : ''} ${isLastInGroup ? 'piq-msg__message--last' : ''}`}>
            <div className="piq-msg__bubble">
              {msg.content && <p className="piq-msg__text">{msg.content}</p>}
              {msg.attachment && (
                <button
                  type="button"
                  className="piq-msg__attachment"
                  onClick={() => {
                    setPdfViewerUrl(msg.attachment.url);
                    setPdfViewerName(msg.attachment.name);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <span className="piq-msg__attachment-name">{msg.attachment.name}</span>
                  <span className="piq-msg__attachment-size">{formatFileSize(msg.attachment.size)}</span>
                </button>
              )}
            </div>
            {isLastInGroup && (
              <span className="piq-msg__meta">{formatMessageTime(msg.timestamp)}</span>
            )}
            {showReadReceipt && (
              <span className={`piq-msg__read-receipt ${msg.read ? 'piq-msg__read-receipt--read' : ''}`}>
                {msg.read ? 'Read' : 'Delivered'}
              </span>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  const totalApproved = (conversations || []).filter(c => c.status === 'approved').length;
  const newCount = (conversations || []).filter(c => c.status === 'approved' && !c.latest_message).length;

  const sortedConversations = (() => {
    let filtered = (conversations || []).filter(c => c.status === 'approved');

    // Apply filter
    if (filterMode === 'active') {
      filtered = filtered.filter(c => c.latest_message);
    } else if (filterMode === 'in_conversation') {
      filtered = filtered.filter(c => c.has_golfer_reply);
    } else if (filterMode === 'awaiting_reply') {
      filtered = filtered.filter(c => c.latest_message && !c.has_golfer_reply);
    } else if (filterMode === 'new') {
      filtered = filtered.filter(c => !c.latest_message);
    }

    // Apply sort
    filtered.sort((a, b) => {
      if (sortMode === 'name') {
        const aName = (a.user?.full_name || '').toLowerCase();
        const bName = (b.user?.full_name || '').toLowerCase();
        return aName.localeCompare(bName);
      }
      // Default: recent (also used as tiebreaker for 'unread')
      const aTime = a.latest_message?.timestamp ? new Date(a.latest_message.timestamp) : new Date(0);
      const bTime = b.latest_message?.timestamp ? new Date(b.latest_message.timestamp) : new Date(0);
      return bTime - aTime;
    });

    return filtered;
  })();

  const showChatOnMobile = isMobile && selectedConversation;

  // The actual component content
  const content = (
    <div
      className={`piq-msg ${showChatOnMobile ? 'piq-msg--chat-open' : ''} ${isMobile ? 'piq-msg--mobile' : ''}`}
      style={{ '--sidebar-width': `${sidebarWidth}px` }}
    >
      {/* Header */}
      {!showChatOnMobile && (
        <div className="piq-msg__header">
          <div className="piq-msg__header-top">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 className="piq-msg__title">Messages</h1>
                <GuideButton sectionKey="conversations" variant="conversations" />
              </div>
              <p className="piq-msg__subtitle">
                {sortedConversations.length === totalApproved
                  ? `${totalApproved} ${totalApproved === 1 ? 'conversation' : 'conversations'}`
                  : `${sortedConversations.length} of ${totalApproved} conversations`
                }
              </p>
            </div>
          </div>
          <div className="piq-msg__filter-tabs">
            {!isMobile && <div className="piq-msg__filter-info-wrap" ref={filterInfoRef}>
              <button
                className="piq-msg__filter-info-btn"
                onClick={() => setShowFilterInfo(!showFilterInfo)}
                aria-label="Filter info"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </button>
              {showFilterInfo && (
                <div className="piq-msg__filter-info-popover">
                  <table className="piq-msg__filter-info-table">
                    <thead>
                      <tr>
                        <th>Filter</th>
                        <th>Shows</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>All</strong></td>
                        <td>Every approved connection</td>
                      </tr>
                      <tr>
                        <td><strong>Active</strong></td>
                        <td>Connections with any messages (club or golfer sent)</td>
                      </tr>
                      <tr>
                        <td><strong>In Conversation</strong></td>
                        <td>Only connections where the golfer has replied</td>
                      </tr>
                      <tr>
                        <td><strong>Awaiting Reply</strong></td>
                        <td>Club has messaged but the golfer hasn't responded yet</td>
                      </tr>
                      <tr>
                        <td><strong>New</strong></td>
                        <td>Connections with no messages yet</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>}
            <button
              className={`piq-msg__filter-tab ${filterMode === 'all' ? 'piq-msg__filter-tab--active' : ''}`}
              onClick={() => setFilterMode('all')}
            >
              All
            </button>
            <button
              className={`piq-msg__filter-tab ${filterMode === 'active' ? 'piq-msg__filter-tab--active' : ''}`}
              onClick={() => setFilterMode('active')}
            >
              Active
            </button>
            <button
              className={`piq-msg__filter-tab ${filterMode === 'in_conversation' ? 'piq-msg__filter-tab--active' : ''}`}
              onClick={() => setFilterMode('in_conversation')}
            >
              In Conversation
            </button>
            <button
              className={`piq-msg__filter-tab ${filterMode === 'awaiting_reply' ? 'piq-msg__filter-tab--active' : ''}`}
              onClick={() => setFilterMode('awaiting_reply')}
            >
              Awaiting Reply
            </button>
            <button
              className={`piq-msg__filter-tab ${filterMode === 'new' ? 'piq-msg__filter-tab--active' : ''}`}
              onClick={() => setFilterMode('new')}
            >
              New
              {newCount > 0 && <span className="piq-msg__filter-tab-badge">{newCount}</span>}
            </button>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="piq-msg__container">
        {/* Conversation List */}
        <div className={`piq-msg__list ${showChatOnMobile ? 'piq-msg__list--hidden' : ''}`}>
          <div className="piq-msg__list-toolbar">
            <select
              className="piq-msg__sort-select"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
            >
              <option value="recent">Most Recent</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>
          {loading ? (
            <div className="piq-msg__loading">
              <div className="piq-msg__spinner"></div>
            </div>
          ) : sortedConversations.length > 0 ? (
            sortedConversations.map((conv) => (
              <div
                key={conv.interaction_request_id}
                className={`piq-msg__item ${selectedConversation?.interaction_request_id === conv.interaction_request_id ? 'piq-msg__item--active' : ''} ${conv.unread_count > 0 ? 'piq-msg__item--unread' : ''}`}
                onClick={() => onSelectConversation(conv)}
              >
                <div className="piq-msg__item-content">
                  <div className="piq-msg__item-header">
                    <h4 className="piq-msg__item-name">{conv.user?.full_name || "Unknown User"}</h4>
                    <span className="piq-msg__item-time">
                      {formatConversationTime(conv.latest_message?.timestamp)}
                    </span>
                  </div>
                  <div className="piq-msg__item-preview">
                    <p className="piq-msg__item-last">
                      {conv.latest_message?.content || "No messages yet"}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="piq-msg__badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="piq-msg__empty">
              <div className="piq-msg__empty-icon">
                <Icons.Message />
              </div>
              {filterMode === 'all' ? (
                <>
                  <h3>No conversations yet</h3>
                  <p>When members approve your connection requests, you can message them here.</p>
                </>
              ) : filterMode === 'active' ? (
                <>
                  <h3>No active conversations</h3>
                  <p>None of your approved connections have exchanged messages yet.</p>
                </>
              ) : filterMode === 'in_conversation' ? (
                <>
                  <h3>No replies yet</h3>
                  <p>None of your golfers have responded to messages yet. Keep the conversation going!</p>
                </>
              ) : filterMode === 'awaiting_reply' ? (
                <>
                  <h3>No pending replies</h3>
                  <p>Every golfer you've messaged has responded. Great engagement!</p>
                </>
              ) : (
                <>
                  <h3>All connections have messages</h3>
                  <p>Every approved connection has an active conversation.</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Prospect Profile Panel (Desktop Only) */}
        {!isMobile && selectedConversation && (
          <div className={`piq-msg__prospect-panel ${showProspectPanel ? 'piq-msg__prospect-panel--open' : ''}`}>
            <div className="piq-msg__prospect-header">
              <h3 className="piq-msg__prospect-title">Prospect Profile</h3>
              <button
                className="piq-msg__prospect-close"
                onClick={() => setShowProspectPanel(false)}
                aria-label="Close prospect profile"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="piq-msg__prospect-body">
              <div className="piq-msg__prospect-hero">
                <div className="piq-msg__prospect-avatar">
                  {(selectedConversation.user?.full_name || "U").charAt(0).toUpperCase()}
                </div>
                <h4 className="piq-msg__prospect-name">{selectedConversation.user?.full_name || "Unknown User"}</h4>
              </div>

              {/* Pipeline Stage */}
              {selectedConversation.status === 'approved' && (
                <div className="piq-msg__prospect-section">
                  <h5 className="piq-msg__prospect-section-label">Stage</h5>
                  <select
                    className="piq-msg__stage-select"
                    value={selectedConversation.pipeline_stage || 'warm_lead'}
                    onChange={(e) => handleStageChange(selectedConversation.interaction_request_id, e.target.value)}
                    style={{
                      backgroundColor: stageColors[selectedConversation.pipeline_stage || 'warm_lead'].bg,
                      color: stageColors[selectedConversation.pipeline_stage || 'warm_lead'].color,
                      borderColor: stageColors[selectedConversation.pipeline_stage || 'warm_lead'].border,
                    }}
                  >
                    {stageOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Contact Information */}
              <div className="piq-msg__prospect-section">
                <h5 className="piq-msg__prospect-section-label">Contact Information</h5>
                {selectedConversation.user?.email && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Email</span>
                    <div className="piq-msg__prospect-field-value-row">
                      <span className="piq-msg__prospect-field-value">{selectedConversation.user.email}</span>
                      <button
                        className="piq-msg__prospect-copy"
                        onClick={() => copyToClipboard(selectedConversation.user.email, 'email')}
                        aria-label="Copy email"
                        title="Copy email"
                      >
                        {copiedField === 'email' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                    {copiedField === 'email' && <span className="piq-msg__prospect-copied">Copied!</span>}
                    <a
                      className="piq-msg__prospect-email-btn"
                      href={`mailto:${selectedConversation.user.email}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      Send Email
                    </a>
                  </div>
                )}
                {selectedConversation.user?.phone && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Phone</span>
                    <div className="piq-msg__prospect-field-value-row">
                      <span className="piq-msg__prospect-field-value">{formatPhone(selectedConversation.user.phone)}</span>
                      <button
                        className="piq-msg__prospect-copy"
                        onClick={() => copyToClipboard(selectedConversation.user.phone, 'phone')}
                        aria-label="Copy phone"
                        title="Copy phone"
                      >
                        {copiedField === 'phone' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                    {copiedField === 'phone' && <span className="piq-msg__prospect-copied">Copied!</span>}
                  </div>
                )}
              </div>

              {/* Demographics */}
              <div className="piq-msg__prospect-section">
                <h5 className="piq-msg__prospect-section-label">Demographics</h5>
                {selectedConversation.user?.demographics?.city && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">City</span>
                    <span className="piq-msg__prospect-field-value">{selectedConversation.user.demographics.city}</span>
                  </div>
                )}
                {selectedConversation.user?.demographics?.age && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Age</span>
                    <span className="piq-msg__prospect-field-value">{selectedConversation.user.demographics.age}</span>
                  </div>
                )}
                {selectedConversation.user?.demographics?.number_of_children != null && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Children</span>
                    <span className="piq-msg__prospect-field-value">{selectedConversation.user.demographics.number_of_children}</span>
                  </div>
                )}
                {selectedConversation.user?.demographics?.household_income && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Household Income</span>
                    <span className="piq-msg__prospect-field-value">{formatIncomeRange(selectedConversation.user.demographics.household_income)}</span>
                  </div>
                )}
                {selectedConversation.user?.demographics?.marital_status && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Marital Status</span>
                    <span className="piq-msg__prospect-field-value" style={{ textTransform: 'capitalize' }}>{selectedConversation.user.demographics.marital_status}</span>
                  </div>
                )}
                {selectedConversation.user?.demographics?.current_club_member && (
                  <div className="piq-msg__prospect-field">
                    <span className="piq-msg__prospect-field-label">Club Member</span>
                    <span className="piq-msg__prospect-field-value" style={{ textTransform: 'capitalize' }}>{selectedConversation.user.demographics.current_club_member}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Member Since Modal */}
        {memberModal && (
          <div className="piq-msg__modal-overlay" onClick={() => setMemberModal(null)}>
            <div className="piq-msg__modal" onClick={(e) => e.stopPropagation()}>
              <h4 className="piq-msg__modal-title">When did they become a member?</h4>
              <div className="piq-msg__modal-fields">
                <select
                  className="piq-msg__modal-select"
                  value={memberModal.month}
                  onChange={(e) => setMemberModal({ ...memberModal, month: parseInt(e.target.value) })}
                >
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  className="piq-msg__modal-select"
                  value={memberModal.year}
                  onChange={(e) => setMemberModal({ ...memberModal, year: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="piq-msg__modal-actions">
                <button className="piq-msg__modal-cancel" onClick={() => setMemberModal(null)}>Cancel</button>
                <button className="piq-msg__modal-confirm" onClick={handleMemberConfirm}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Window */}
        <div className={`piq-msg__chat ${!selectedConversation ? 'piq-msg__chat--empty' : ''}`}>
          {selectedConversation ? (
            <>
              <div className="piq-msg__chat-header">
                {isMobile && (
                  <button
                    className="piq-msg__back-btn"
                    onClick={() => onSelectConversation(null)}
                    aria-label="Back to conversations"
                  >
                    <Icons.ChevronLeft />
                  </button>
                )}
                <div className="piq-msg__chat-user">
                  {isMobile ? (
                    <div className="piq-msg__chat-avatar">
                      {(selectedConversation.user?.full_name || "U").charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <button
                      className={`piq-msg__profile-btn ${showProspectPanel ? 'piq-msg__profile-btn--active' : ''}`}
                      onClick={() => setShowProspectPanel(!showProspectPanel)}
                      aria-label="View prospect profile"
                      title="View prospect profile"
                    >
                      {(selectedConversation.user?.full_name || "U").charAt(0).toUpperCase()}
                      <span className="piq-msg__profile-btn-badge">
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </span>
                    </button>
                  )}
                  <div className="piq-msg__chat-details">
                    <h4>{selectedConversation.user?.full_name || "Unknown User"}</h4>
                    <span className="piq-msg__chat-meta">
                      {selectedConversation.user?.demographics?.age && `${selectedConversation.user.demographics.age} years old`}
                      {selectedConversation.user?.demographics?.current_club_member === "yes" && " • Club Member"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="piq-msg__messages">
                {messagesLoading ? (
                  <div className="piq-msg__loading">
                    <div className="piq-msg__spinner"></div>
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length > 0 ? (
                  <>
                    {renderMessages()}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="piq-msg__chat-empty">
                    <div className="piq-msg__chat-empty-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        <path d="M8 10h.01"></path>
                        <path d="M12 10h.01"></path>
                        <path d="M16 10h.01"></path>
                      </svg>
                    </div>
                    <h3>Start the conversation</h3>
                    <p>Say hello to {selectedConversation.user?.full_name?.split(' ')[0] || 'this member'}!</p>
                  </div>
                )}
              </div>

              <div className="piq-msg__input-area">
                {attachedFile && (
                  <div className="piq-msg__file-preview">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    <span className="piq-msg__file-preview-name">{attachedFile.name}</span>
                    <span className="piq-msg__file-preview-size">{formatFileSize(attachedFile.size)}</span>
                    <button
                      className="piq-msg__file-preview-remove"
                      onClick={() => setAttachedFile(null)}
                      aria-label="Remove attachment"
                    >
                      &times;
                    </button>
                  </div>
                )}
                <div className="piq-msg__input-wrapper">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    className="piq-msg__attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    aria-label="Attach PDF"
                    title="Attach PDF"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                    </svg>
                  </button>
                  <textarea
                    ref={inputRef}
                    className="piq-msg__input"
                    placeholder="Type your message..."
                    value={messageInput}
                    onChange={(e) => {
                      if (e.target.value.length <= 1000) {
                        setMessageInput(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    rows={1}
                  />
                  {messageInput.length > 800 && (
                    <span className={`piq-msg__char-count ${messageInput.length >= 1000 ? 'piq-msg__char-count--limit' : ''}`}>
                      {messageInput.length}/1000
                    </span>
                  )}
                </div>
                <button
                  className="piq-msg__send-btn"
                  onClick={sendMessage}
                  disabled={(!messageInput.trim() && !attachedFile) || sending}
                  aria-label="Send message"
                >
                  {sending ? (
                    <div className="piq-msg__send-spinner"></div>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="piq-msg__no-selection">
              <div className="piq-msg__no-selection-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <h3>Select a conversation</h3>
              <p>Choose someone from the list to start messaging</p>
            </div>
          )}
        </div>
      </div>
      {/* PDF Viewer Modal */}
      {pdfViewerUrl && (
        <div className="piq-msg__pdf-overlay" onClick={() => { setPdfViewerUrl(null); setPdfViewerName(''); }}>
          <div className="piq-msg__pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="piq-msg__pdf-header">
              <h3>{pdfViewerName || 'PDF'}</h3>
              <button
                type="button"
                className="piq-msg__pdf-close"
                onClick={() => { setPdfViewerUrl(null); setPdfViewerName(''); }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="piq-msg__pdf-body">
              <iframe
                src={pdfViewerUrl}
                title={pdfViewerName || 'PDF'}
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // On mobile, render inline (no portal) so it flows naturally after subscription banner
  // On desktop, use Portal to render outside React tree for fixed positioning
  if (isMobile) {
    return content;
  }
  return ReactDOM.createPortal(content, document.body);
}

export default ConversationsSection;
